import { useState, useEffect, useCallback } from 'react';
import { getBackendUrl } from '../utils/getBackendUrl';

const API = getBackendUrl() + '/api';

export const usePushNotifications = (user) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSupported, setIsSupported] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);

  useEffect(() => {
    const checkSupport = async () => {
      // Check if browser supports service workers and push notifications
      const supported = 'serviceWorker' in navigator && 'PushManager' in window;
      setIsSupported(supported);

      if (!supported) {
        setError('Push notifications are not supported in this browser');
        return;
      }

      // Register service worker
      try {
        const reg = await navigator.serviceWorker.register('/service-worker.js', {
          scope: '/'
        });
        setRegistration(reg);

        // Check if already subscribed
        const subscription = await reg.pushManager.getSubscription();
        setIsSubscribed(!!subscription);
        
        // Check if permission is denied
        if (Notification.permission === 'denied') {
          setPermissionDenied(true);
          setError('Notifications are blocked in your browser settings');
        }
      } catch (err) {
        console.error('Error registering service worker:', err);
        setError('Failed to register service worker');
      }
    };

    if (user) {
      checkSupport();
    }
  }, [user]);

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/\-/g, '+').replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  const subscribe = useCallback(async () => {
    if (!isSupported) {
      setError('Push notifications not supported');
      return false;
    }

    if (!registration) {
      setError('Service worker not registered. Please refresh the page.');
      return false;
    }

    try {
      setIsLoading(true);
      setError(null);
      setPermissionDenied(false);

      // Check notification permission first
      console.log('Current notification permission:', Notification.permission);

      if (Notification.permission === 'denied') {
        setPermissionDenied(true);
        setError('Notifications are blocked. Please enable them in your browser settings.');
        setIsLoading(false);
        return false;
      }

      // Request permission if not granted
      if (Notification.permission !== 'granted') {
        console.log('Requesting notification permission...');
        const permission = await Notification.requestPermission();
        console.log('Permission result:', permission);
        
        if (permission !== 'granted') {
          setPermissionDenied(true);
          setError('Notification permission denied. Please enable in browser settings.');
          setIsLoading(false);
          return false;
        }
      }

      // Get VAPID public key from backend
      console.log('Fetching VAPID public key from:', `${API}/push/public-key`);
      const keyResponse = await fetch(`${API}/push/public-key`);
      if (!keyResponse.ok) {
        throw new Error('Failed to get public key from server');
      }
      const { publicKey } = await keyResponse.json();
      console.log('Got VAPID public key');

      // Convert key to Uint8Array
      const applicationServerKey = urlBase64ToUint8Array(publicKey);
      console.log('Converted key to Uint8Array');

      // Subscribe to push notifications
      console.log('Subscribing to push manager...');
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey
      });
      console.log('Push subscription successful');

      // Convert keys to base64
      const subscriptionData = {
        endpoint: subscription.endpoint,
        keys: {
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth')))),
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh'))))
        },
        user_id: user.id
      };

      // Send subscription to backend with auth token
      const token = localStorage.getItem('token');
      console.log('Sending subscription to backend...');
      const response = await fetch(`${API}/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(subscriptionData)
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Backend error:', errorData);
        throw new Error(`Failed to save to server (Error ${response.status})`);
      }

      const result = await response.json();
      console.log('Backend response:', result);

      setIsSubscribed(true);
      console.log('✅ Push notifications enabled successfully!');
      return true;
    } catch (err) {
      console.error('Error subscribing to push notifications:', err);
      
      // Provide specific error messages
      let errorMessage = '';
      if (err.message.includes('permission')) {
        errorMessage = 'Permission denied. Enable notifications in browser settings.';
        setPermissionDenied(true);
      } else if (err.message.includes('public key')) {
        errorMessage = 'Cannot connect to server. Check internet connection.';
      } else if (err.message.includes('save to server') || err.message.includes('Error 4')) {
        errorMessage = 'Server error. You may need to log in again.';
      } else if (err.message.includes('Service Worker')) {
        errorMessage = 'Service worker error. Close and reopen the app.';
      } else {
        errorMessage = 'Error: ' + err.message;
      }
      
      setError(errorMessage);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, registration, user, API]);

  const unsubscribe = useCallback(async () => {
    if (!registration) {
      setError('Service worker not registered');
      return false;
    }

    setIsLoading(true);
    setError(null);

    try {
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        throw new Error('Not subscribed to push notifications');
      }

      // Unsubscribe from backend with auth token
      const token = localStorage.getItem('token');
      await fetch(`${API}/push/unsubscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ endpoint: subscription.endpoint })
      });

      // Unsubscribe from push manager
      await subscription.unsubscribe();

      setIsSubscribed(false);
      setIsLoading(false);
      return true;
    } catch (err) {
      console.error('Error unsubscribing from push notifications:', err);
      setError(err.message);
      setIsLoading(false);
      return false;
    }
  }, [registration, API]);

  const checkSubscription = useCallback(async () => {
    if (!registration) {
      return false;
    }

    try {
      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        return false;
      }

      // Check with backend if this subscription is active
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API}/push/subscription?endpoint=${encodeURIComponent(subscription.endpoint)}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (!response.ok) {
        return false;
      }

      const data = await response.json();
      return data.active || false;
    } catch (err) {
      console.error('Error checking subscription:', err);
      return false;
    }
  }, [registration, API]);

  return {
    isSubscribed,
    isSupported,
    isLoading,
    error,
    permissionDenied,
    subscribe,
    unsubscribe,
    checkSubscription
  };
};
